/**
 * Pure helpers for Testing.jsx so the state-transition logic can be
 * unit-tested without rendering the full component (which carries auth
 * context, API client, multiple effects).
 */

/**
 * Apply a canonical category pick to an item. Returns a NEW item object
 * with all four productType* fields synchronised so the testing-page gate
 * (canSubmitTesting + the rest-of-form visibility check) unlocks.
 *
 * History: a previous version of Testing.jsx updated productType +
 * testingBucket but left productTypeNeedsReview=true from the original
 * enrichment, so picking from the canonical-66 selector silently failed
 * to clear the red Alert. The pure helper makes the contract explicit.
 *
 * @param {object} item — current item object
 * @param {string} productType — canonical sub-category (caller guarantees canonical)
 * @param {string|null} bucket — testing bucket for productType (from server)
 * @returns {object} new item with synchronised productType* fields
 */
export function applyCategoryPick(item, productType, bucket) {
  return {
    ...item,
    productType,
    testingBucket: bucket,
    productTypeSource: 'canonical',
    productTypeNeedsReview: false,
    productTypeSuggested: null,
  }
}

/**
 * The category-derived part of the Testing.jsx "can the tech proceed?"
 * gate. Returns true ONLY if the item has a canonical productType set.
 *
 * Used by canSubmit() and by the rest-of-form visibility check
 * `currentItem.productType && !needsCategoryReview` in Testing.jsx.
 *
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
export function isCategoryReadyForTesting(item) {
  if (!item) return false
  if (!item.productType) return false
  if (item.productTypeNeedsReview === true) return false
  return true
}

/**
 * The deal identifier to SHOW the operator — not the one to save with.
 *
 * Since the Prism cutover, `deal.id` is the quote UUID because the save
 * endpoints round-trip it. The UUID is meaningless to an operator, so it is
 * NEVER shown. We prefer the reference code (the TI-#### an operator quotes
 * back to a customer), then the legacy HubSpot deal id, then nothing.
 *
 * Note: this is reference-first by explicit operator preference, which differs
 * from deal_search_service's COALESCE(hubspot_dealid, reference_code) — a
 * legacy deal that carries both shows its reference here but its HubSpot id in
 * deal search. Shared by the Testing and Receiving warehouse pages.
 *
 * @param {object|null|undefined} deal — testing/receiving search result
 * @returns {string} identifier for display; '' when there is no usable id
 */
export function displayDealId(deal) {
  if (!deal) return ''
  return deal.reference || deal.hubspotDealId || ''
}

/**
 * The <DealLink> props for a testing/receiving row: the id to open the deal by
 * (HubSpot id, else the quote id) and the text to show (see displayDealId).
 *
 * @param {object|null|undefined} deal: a testing/receiving search result
 * @returns {{ dealId: string|null, reference: string }}
 */
export function dealLinkProps(deal) {
  if (!deal) return { dealId: null, reference: '' }
  return { dealId: deal.hubspotDealId || deal.id, reference: displayDealId(deal) }
}

const TESTING_LEVELS_ELECTRICAL = ['Fully Tested', 'Partially Tested', 'Power On Only', 'Untested']
const TESTING_LEVELS_NON_ELECTRICAL = ['Fully Tested', 'Partially Tested', 'Untested']

/**
 * An item is electrical only when its checklist says so. No checklist (not
 * loaded, fetch failed, unknown type) is not electrical, so the form never
 * offers "Power On Only" for something that may have no power.
 *
 * @param {object|null|undefined} checklist — checklist from /api/testing/checklist
 * @returns {boolean}
 */
export function isElectricalChecklist(checklist) {
  return checklist?.electrical === true
}

/**
 * The testing levels a form may offer for a checklist.
 *
 * @param {object|null|undefined} checklist
 * @returns {string[]}
 */
export function testingLevelsFor(checklist) {
  return isElectricalChecklist(checklist) ? TESTING_LEVELS_ELECTRICAL : TESTING_LEVELS_NON_ELECTRICAL
}
